import { z } from "zod";

/**
 * The mapping run that turns embedded sources into the brand-voice model:
 * attempt lifecycle, which model parent a produced row hangs off, and the
 * definition tables a mapped row and its evidence can target.
 */

// Definition-table fields that may carry source evidence. This lives with the
// enum catalog so table and field value sets are discoverable in one place.
export const TOV_EVIDENCE_TARGETS = {
  tov_traits: ["label", "description", "target_strength"],
  tov_trait_boundaries: ["text"],
  tov_tone_dimensions: ["label", "low_label", "high_label"],
  tov_rules: ["title", "description", "rationale"],
  tov_lexicon: ["term", "replacement", "reason"],
  tov_examples: ["output_text", "explanation"],
  tov_audiences: ["description", "needs", "pain_points"],
  tov_content_pillars: ["description", "objective"],
  tov_journey_stages: ["description"],
  tov_channels: ["label", "description"],
  tov_channel_placements: ["role"],
  tov_channel_objectives: ["objective"],
  tov_channel_content_formats: ["format_label"],
  tov_channel_metrics: ["metric_label", "measurement_context"],
  tov_channel_routes: ["label"],
  tov_visual_guidelines: ["title", "description"],
} as const;
export type TovEvidenceTargetTable = keyof typeof TOV_EVIDENCE_TARGETS;
export type TovEvidenceTargetField<T extends TovEvidenceTargetTable> =
  (typeof TOV_EVIDENCE_TARGETS)[T][number];

export const TOV_EVIDENCE_TARGET_TABLES = Object.keys(TOV_EVIDENCE_TARGETS) as [
  TovEvidenceTargetTable,
  ...TovEvidenceTargetTable[],
];
export const TovEvidenceTargetTableSchema = z.enum(TOV_EVIDENCE_TARGET_TABLES);

export const TOV_MAPPER_MODEL_PARENTS = [
  "trait",
  "channel",
  "placement",
] as const;
export const TOV_MAPPER_MODEL_PARENT = {
  TRAIT: "trait",
  CHANNEL: "channel",
  PLACEMENT: "placement",
} as const satisfies Record<string, TovMapperModelParent>;
export const TovMapperModelParentSchema = z
  .enum(TOV_MAPPER_MODEL_PARENTS)
  .nullable();
export type TovMapperModelParent = z.infer<typeof TovMapperModelParentSchema>;

// Derived, never stored: an attempt's status folds over its insert-only event
// log, and `superseded` simply means "not the Version's latest attempt".
export const TOV_MAP_ATTEMPT_STATUSES = [
  "running",
  "succeeded",
  "failed",
  "superseded",
  "incompatible",
] as const;
export const TOV_MAP_ATTEMPT_STATUS = {
  RUNNING: "running",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
  SUPERSEDED: "superseded",
  INCOMPATIBLE: "incompatible",
} as const satisfies Record<string, TovMapAttemptStatus>;
export const TovMapAttemptStatusSchema = z.enum(TOV_MAP_ATTEMPT_STATUSES);
export type TovMapAttemptStatus = z.infer<typeof TovMapAttemptStatusSchema>;

export const TOV_MAP_ATTEMPT_EVENT_TYPES = [
  "started",
  "resumed",
  "succeeded",
  "failed",
  "incompatible",
] as const;
export const TOV_MAP_ATTEMPT_EVENT_TYPE = {
  STARTED: "started",
  RESUMED: "resumed",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
  INCOMPATIBLE: "incompatible",
} as const satisfies Record<string, TovMapAttemptEventType>;
export const TovMapAttemptEventTypeSchema = z.enum(TOV_MAP_ATTEMPT_EVENT_TYPES);
export type TovMapAttemptEventType = z.infer<
  typeof TovMapAttemptEventTypeSchema
>;

export const TOV_MAPPER_GROUP_KEYS = [
  "voice",
  "strategy",
  "channels",
  "visual",
] as const;
export const TovMapperGroupKeySchema = z.enum(TOV_MAPPER_GROUP_KEYS);
export type TovMapperGroupKey = z.infer<typeof TovMapperGroupKeySchema>;

export const TOV_VERSION_SCOPED_TABLES = [
  "tov_tone_dimensions",
  "tov_rules",
  "tov_lexicon",
  "tov_examples",
  "tov_audiences",
  "tov_content_pillars",
  "tov_journey_stages",
  "tov_channels",
  "tov_visual_guidelines",
] as const satisfies readonly TovEvidenceTargetTable[];
export const TOV_VERSION_SCOPED_TABLE = {
  TOV_TONE_DIMENSIONS: "tov_tone_dimensions",
  TOV_RULES: "tov_rules",
  TOV_LEXICON: "tov_lexicon",
  TOV_EXAMPLES: "tov_examples",
  TOV_AUDIENCES: "tov_audiences",
  TOV_CONTENT_PILLARS: "tov_content_pillars",
  TOV_JOURNEY_STAGES: "tov_journey_stages",
  TOV_CHANNELS: "tov_channels",
  TOV_VISUAL_GUIDELINES: "tov_visual_guidelines",
} as const satisfies Record<string, TovVersionScopedTable>;
export const TovVersionScopedTableSchema = z.enum(TOV_VERSION_SCOPED_TABLES);
export type TovVersionScopedTable = z.infer<typeof TovVersionScopedTableSchema>;

export const TOV_PLACEMENT_CHILD_TABLES = [
  "tov_channel_objectives",
  "tov_channel_content_formats",
  "tov_channel_metrics",
  "tov_channel_routes",
  "tov_channel_rules",
] as const;
export const TOV_PLACEMENT_CHILD_TABLE = {
  TOV_CHANNEL_OBJECTIVES: "tov_channel_objectives",
  TOV_CHANNEL_CONTENT_FORMATS: "tov_channel_content_formats",
  TOV_CHANNEL_METRICS: "tov_channel_metrics",
  TOV_CHANNEL_ROUTES: "tov_channel_routes",
  TOV_CHANNEL_RULES: "tov_channel_rules",
} as const satisfies Record<string, TovPlacementChildTable>;
export const TovPlacementChildTableSchema = z.enum(TOV_PLACEMENT_CHILD_TABLES);
export type TovPlacementChildTable = z.infer<
  typeof TovPlacementChildTableSchema
>;

// What registering a worker's contact with a mapping attempt did. `superseded`
// means a newer attempt owns the Version; `incompatible` means the checkpoints
// on disk cannot be resumed against the current model.
export const TOV_MAP_START_OUTCOMES = [
  "started",
  "resumed",
  "superseded",
  "incompatible",
] as const;
export const TovMapStartOutcomeSchema = z.enum(TOV_MAP_START_OUTCOMES);
export type TovMapStartOutcome = z.infer<typeof TovMapStartOutcomeSchema>;

// What asking for a mapping run did. `coalesced` = a run for this Version is
// already in flight, so no second run was queued (duplicate LLM spend guard).
// `resumed` = the latest failed attempt was republished so its durable
// checkpoints are reused instead of re-extracting every page and group.
export const TOV_MAP_ENQUEUE_OUTCOMES = [
  "enqueued",
  "coalesced",
  "resumed",
] as const;
export const TovMapEnqueueOutcomeSchema = z.enum(TOV_MAP_ENQUEUE_OUTCOMES);
export type TovMapEnqueueOutcome = z.infer<typeof TovMapEnqueueOutcomeSchema>;
export const TOV_MAP_START_OUTCOME = {
  STARTED: "started",
  RESUMED: "resumed",
  SUPERSEDED: "superseded",
  INCOMPATIBLE: "incompatible",
} as const satisfies Record<string, TovMapStartOutcome>;

export const TOV_MAP_ENQUEUE_OUTCOME = {
  ENQUEUED: "enqueued",
  COALESCED: "coalesced",
  RESUMED: "resumed",
} as const satisfies Record<string, TovMapEnqueueOutcome>;
